import * as Yup from 'yup';
import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Link, Stack, Alert, IconButton, InputAdornment } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { PATH_AUTH } from '../../../../shared/routes/paths';
import useIsMountedRef from '../../../../shared/hooks/useIsMountedRef';
import { Iconify } from '../../../../shared/components/base';
import { FormProvider, RHFTextField } from '../../../../shared/components/hook-form';
import useLoginForm from '../hooks/useLoginForm';

import { routeKeys } from '../../../../shared/utils/constants';

export default function LoginForm() {
  const { login, loginWithGoogle, isLoggingIn } = useLoginForm();
  const navigate = useNavigate();
  const isMountedRef = useIsMountedRef();
  const [showPassword, setShowPassword] = useState(false);

  const LoginSchema = Yup.object().shape({
    email: Yup.string().email('Email must be a valid email address').required('Email is required'),
    password: Yup.string().required('Password is required'),
  });

  const defaultValues = {
    email: '',
    password: '',
    remember: true,
  };

  const methods = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues,
  });

  const {
    reset,
    setError,
    handleSubmit,
    formState: { errors },
  } = methods;

  const onSubmit = async (data) => {
    try {
      login(data.email.toLowerCase(), data.password);
    } catch (error) {
      console.error(error);
      reset();

      if (isMountedRef.current) {
        setError('afterSubmit', { ...error, message: error.message });
      }
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      await loginWithGoogle();
    } catch (error) {
      console.error('Google login failed:', error);
      if (isMountedRef.current) {
        setError('afterSubmit', { ...error, message: error.message });
      }
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        {!!errors.afterSubmit && <Alert severity="error">{errors.afterSubmit.message}</Alert>}

        <RHFTextField name="email" label="Email address" />

        <RHFTextField
          name="password"
          label="Password"
          type={showPassword ? 'text' : 'password'}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                  <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ my: 2 }}>
        <Link component={RouterLink} variant="subtitle2" to={PATH_AUTH.resetPassword}>
          Forgot Password?
        </Link>
        <Button onClick={() => navigate(routeKeys.auth + routeKeys.register)}>Register account</Button>
      </Stack>

      <LoadingButton fullWidth size="large" type="submit" variant="contained" loading={isLoggingIn}>
        Login
      </LoadingButton>

      {/* TO-DO: Display Continue with Google after fixing CORS policy */}
      {/* 
      <Divider sx={{ my: 3 }} />
      <Button
        fullWidth
        size="large"
        color="inherit"
        variant="outlined"
        onClick={handleGoogleSignIn}
        startIcon={<Iconify icon="eva:google-fill" />}
      >
        Continue with Google
      </Button> */}
    </FormProvider>
  );
}
